import type { CSSProperties } from "react"
import { cva } from "class-variance-authority"

import { colorToken } from "../../../lib/utils"

/**
 * Maps {@link IconSemanticColor} to Tailwind `text-*` classes aligned with `@nexus/ui` design tokens (`design-system.css` / `@theme inline`).
 */
export const iconSemanticColorClasses = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  primary: "text-primary",
  primaryForeground: "text-primary-foreground",
  secondaryForeground: "text-secondary-foreground",
  accent: "text-accent-foreground",
  destructive: "text-destructive",
  destructiveForeground: "text-destructive-foreground",
  card: "text-card-foreground",
  popover: "text-popover-foreground",
  sidebar: "text-sidebar-foreground",
  sidebarPrimaryForeground: "text-sidebar-primary-foreground",
  inherit: "text-inherit",
} as const

export type IconSemanticColor = keyof typeof iconSemanticColorClasses

export const ICON_SEMANTIC_COLOR_OPTIONS = Object.keys(iconSemanticColorClasses) as IconSemanticColor[]

export const iconSemanticVariants = cva("", {
  variants: {
    color: iconSemanticColorClasses,
  },
  defaultVariants: {
    color: "default",
  },
})

/** CSS custom property name for icon color: `--foreground` or `foreground` (prefix added). */
export function normalizeCssVar(token: string): string {
  const t = token.trim()
  return t.startsWith("--") ? t : `--${t}`
}

/**
 * Resolved `style.color` when using `colorVar` on `Icon` instead of semantic `color` classes.
 * `colorVarOpacity` defaults to **1** (full `var(--…)`). Values &lt; 1 use {@link colorToken}.
 */
export function resolveIconColorVarStyle(
  colorVar: string,
  colorVarOpacity: number = 1,
): Pick<CSSProperties, "color"> {
  const key = normalizeCssVar(colorVar)
  if (colorVarOpacity < 1) {
    return { color: colorToken(key, colorVarOpacity) }
  }
  return { color: `var(${key})` }
}
